/**
 * money.ts — exact dollars↔cents conversion. No float arithmetic on money, ever.
 *
 * ── Why this exists ─────────────────────────────────────────────────────────────
 * `Math.round(1.005 * 100)` is **100**, not 101. `1.005` has no exact binary
 * representation and the product lands at `100.49999999999999`, so the half-cent
 * rounds DOWN. Every dollar amount ending in a half-cent is wrong by a cent that
 * way, and always in the same direction — the hauler loses it.
 *
 * It is not a rare shape either. Sweeping realistic oilfield values (quantity
 * 0.000–400.999 at three decimals × eleven per-BBL/hourly cent rates, 4,411,000
 * pairs) found **31,948 divergences, 100% of them under-billing**. `0.145 bbl` at
 * `$1.00/bbl` bills 14¢ instead of 15¢.
 *
 * ── The one thing that must not be shortcut ─────────────────────────────────────
 * Every function here reads the DECIMAL DIGITS of its input. A float never touches
 * a monetary value on its way to an integer. That is the whole point: a helper that
 * internally did `Number(s) * 100` would have the identical signature, pass a
 * casual test, and reintroduce the bug.
 *
 * Corollary, and the reason `lineAmountCents` takes a STRING: once a decimal
 * quantity has been through `Number()`, the information needed to be exact is
 * already gone. Postgres hands `numeric` columns back as strings — pass them
 * through unparsed.
 *
 * ── What is deliberately NOT here ───────────────────────────────────────────────
 * Integer-cent arithmetic needs no help: summing cents, `cents / 100` for display,
 * and `Math.round((subtotalCents * taxRateBps) / 10_000)` are all exact, the last
 * because both inputs are integers so the product is exact and the half-way cases
 * land on dyadic rationals. Measured: 0 divergences across 1,600,000
 * (subtotal, bps) pairs. Do not "fix" those — see `money.test.ts`, which pins it.
 *
 * Half-up is the rounding mode throughout, matching `parseMoneyDollars` in
 * api/src/import-templates/conventions.ts (Session 3, Phase 20) and the
 * `ROUND_HALF_UP` the invoice PDF states.
 */

/** A money parse either yields exact cents, or says why it could not. */
export type MoneyParse =
  | { readonly ok: true; readonly cents: number }
  /** `blank` is kept distinct from `invalid` so callers can mean "inherit the default". */
  | { readonly ok: false; readonly reason: 'blank' | 'invalid' | 'too-large' };

/** `$1,234.50` → `1234.50`. Currency symbol, thousands separators and spaces only. */
function normalize(raw: string): string {
  return raw.trim().replace(/[$,\s]/g, '');
}

/**
 * Dollars as typed by a human → integer cents, exactly.
 *
 * Accepts an optional `$`, thousands separators, and any number of decimals;
 * rounds half-up at the third decimal. `1.005` → `101`, not `100`.
 */
export function parseDollarsToCents(raw: string): MoneyParse {
  const t = normalize(raw);
  if (t === '') return { ok: false, reason: 'blank' };
  if (!/^-?\d+(?:\.\d+)?$/.test(t)) return { ok: false, reason: 'invalid' };

  const negative = t.startsWith('-');
  const unsigned = negative ? t.slice(1) : t;
  const dot = unsigned.indexOf('.');
  const whole = dot === -1 ? unsigned : unsigned.slice(0, dot);
  const frac = dot === -1 ? '' : unsigned.slice(dot + 1);

  // Two digits of cents plus the single digit we round on. Digits, not arithmetic.
  const digits = `${frac}000`.slice(0, 3);
  let cents =
    BigInt(whole) * 100n + BigInt(digits.slice(0, 2)) + (Number(digits[2]) >= 5 ? 1n : 0n);
  if (negative) cents = -cents;

  if (cents > BigInt(Number.MAX_SAFE_INTEGER) || cents < -BigInt(Number.MAX_SAFE_INTEGER)) {
    return { ok: false, reason: 'too-large' };
  }
  return { ok: true, cents: Number(cents) };
}

/**
 * True when `raw` carries more precision than a cent can hold (a third decimal or
 * beyond), so a UI can warn that `1.005` will be stored as `$1.01` instead of
 * silently keeping a value the database cannot represent.
 */
export function hasSubCentPrecision(raw: string): boolean {
  const t = normalize(raw);
  const dot = t.indexOf('.');
  return dot !== -1 && t.slice(dot + 1).replace(/0+$/, '').length > 2;
}

/**
 * A line amount in integer cents: a decimal `quantity` × an integer-cent `rate`,
 * half-up, exactly.
 *
 * `quantity` is a STRING on purpose — `billable_events.quantity` and
 * `haul_capture_line_items.quantity` are Postgres `numeric` and arrive as strings.
 * Calling `Number()` on one before getting here is the bug, not a convenience.
 *
 * Throws on a non-decimal quantity or a non-integer rate rather than guessing:
 * a malformed money input must not be rounded into something plausible.
 */
export function lineAmountCents(quantity: string, rateCents: number): number {
  return scaledLineAmount('lineAmountCents', 'integer cents', quantity, rateCents, 1n);
}

/**
 * A line amount in integer cents from a SUB-CENT rate: a decimal `quantity` × a rate
 * in **hundredths of a cent** (so `$0.425` is `4250`), half-up, exactly, rounded to the
 * cent only once at the end (DEC-331: disposal facilities charge fractions of a cent).
 *
 * `130` bbl × `4250` → `5525` ($55.25); `3` × `4250` → `128` (127.5¢ rounds up, where the
 * float route `Math.round(3 * 0.425 * 100)` gives 127). Same string-quantity rule as above.
 */
export function lineAmountCentsAtHundredthsCent(
  quantity: string,
  rateHundredthsCent: number,
): number {
  return scaledLineAmount(
    'lineAmountCentsAtHundredthsCent',
    'an integer rate in hundredths of a cent',
    quantity,
    rateHundredthsCent,
    100n,
  );
}

/** quantity × rate ÷ divisor, half-up to integer cents — exact, in BigInt. */
function scaledLineAmount(
  fn: string,
  rateKind: string,
  quantity: string,
  rate: number,
  divisor: bigint,
): number {
  if (!Number.isInteger(rate)) {
    throw new Error(`${fn} expects ${rateKind}, received ${String(rate)}`);
  }
  const t = normalize(quantity);
  if (!/^-?\d+(?:\.\d+)?$/.test(t)) {
    throw new Error(`${fn} expects a decimal quantity, received ${String(quantity)}`);
  }

  const negative = t.startsWith('-');
  const unsigned = negative ? t.slice(1) : t;
  const dot = unsigned.indexOf('.');
  const whole = dot === -1 ? unsigned : unsigned.slice(0, dot);
  const frac = dot === -1 ? '' : unsigned.slice(dot + 1);

  // Scale the quantity to an integer, multiply, then divide back out — all in
  // BigInt, so the half-way case is decided on exact digits.
  const qtyScale = 10n ** BigInt(frac.length);
  const scaledQty = BigInt(whole) * qtyScale + (frac === '' ? 0n : BigInt(frac));
  const product = scaledQty * BigInt(Math.abs(rate));
  const scale = qtyScale * divisor;

  const q = product / scale;
  const rem = product % scale;
  // Half-up: compare the remainder against half the scale without dividing.
  let cents = rem * 2n >= scale ? q + 1n : q;
  if (negative !== rate < 0) cents = -cents;

  if (cents > BigInt(Number.MAX_SAFE_INTEGER) || cents < -BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new Error(`${fn} overflowed: ${quantity} × ${String(rate)}`);
  }
  return Number(cents);
}

/** A sub-cent price parse: exact hundredths of a cent, or why it could not be read. */
export type SubCentParse =
  | { readonly ok: true; readonly hundredthsCent: number }
  | { readonly ok: false; readonly reason: 'blank' | 'invalid' | 'too-precise' | 'too-large' };

/**
 * A per-unit price typed in dollars → integer **hundredths of a cent**, exactly
 * (DEC-331). `$0.425` → `4250`, `0.45` → `4500`, `$1` → `10000`.
 *
 * Up to four decimals are kept (the unit's precision, the spec's DECIMAL(8,4)). A
 * fifth significant decimal is refused as `too-precise` rather than rounded: a price
 * is entered once, and silently changing it would be wrong on every load. Negative
 * prices are `invalid`.
 */
export function parseDollarsToHundredthsCent(raw: string): SubCentParse {
  const t = normalize(raw);
  if (t === '') return { ok: false, reason: 'blank' };
  if (!/^\d+(?:\.\d+)?$/.test(t)) return { ok: false, reason: 'invalid' };
  const dot = t.indexOf('.');
  const whole = dot === -1 ? t : t.slice(0, dot);
  const frac = dot === -1 ? '' : t.slice(dot + 1).replace(/0+$/, '');
  if (frac.length > 4) return { ok: false, reason: 'too-precise' };
  const units = BigInt(whole) * 10_000n + BigInt(`${frac}0000`.slice(0, 4));
  if (units > BigInt(Number.MAX_SAFE_INTEGER)) return { ok: false, reason: 'too-large' };
  return { ok: true, hundredthsCent: Number(units) };
}

/** Hundredths of a cent → dollars with two to four decimals, exact: `4250` → `0.425`. */
export function formatHundredthsCentAsDollars(units: number): string {
  if (!Number.isSafeInteger(units) || units < 0) {
    throw new Error(
      `formatHundredthsCentAsDollars expects a non-negative integer, received ${String(units)}`,
    );
  }
  let frac = String(units % 10_000).padStart(4, '0');
  while (frac.length > 2 && frac.endsWith('0')) frac = frac.slice(0, -1);
  return `${Math.floor(units / 10_000)}.${frac}`;
}

/** Integer cents → a plain `1234.50` decimal string. Exact: cents are the atom. */
export function formatCentsAsDollars(cents: number): string {
  if (!Number.isInteger(cents)) {
    throw new Error(`formatCentsAsDollars expects integer cents, received ${String(cents)}`);
  }
  const negative = cents < 0;
  const abs = Math.abs(cents);
  return `${negative ? '-' : ''}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

/** a × b ÷ d, half-up to an integer — exact for non-negative safe-integer inputs. */
export function mulDivHalfUp(a: number, b: number, d: number): number {
  const n = BigInt(a) * BigInt(b);
  const den = BigInt(d);
  const q = n / den;
  const out = (n % den) * 2n >= den ? q + 1n : q;
  if (out > BigInt(Number.MAX_SAFE_INTEGER)) throw new Error('mulDivHalfUp overflowed');
  return Number(out);
}

/**
 * A price change (gap 1 D-13 "Raise prices", OPEN-213 D-19): add whole cents, or a percentage in
 * basis points (1% = 100 bp, so 2.5% is 250). Either may be negative (a cut).
 */
export type PriceChange =
  | { readonly mode: 'DOLLARS'; readonly cents: number }
  | { readonly mode: 'PERCENT'; readonly basisPoints: number };

/** A typed percentage: exact basis points, or the message to show under the field. */
export type PercentParse =
  | { readonly ok: true; readonly bp: number }
  | { readonly ok: false; readonly error: string };

const PERCENT_TEXT = /^([+-]?)(\d*)(?:\.(\d*))?%?$/;
/** The highest raise, 1000%, in basis points. */
const MAX_RAISE_BP = 100_000;

/**
 * "5", "2.5", "-3.25", "5%" -> basis points (integer); max 2 decimals; > -100 and <= 1000 (%).
 * String math only: the digits are read, never multiplied as a float.
 */
export function parsePercentToBasisPoints(raw: string): PercentParse {
  const generic = { ok: false, error: 'Enter a percentage, e.g. 5 or 2.5.' } as const;
  const m = PERCENT_TEXT.exec(raw.trim());
  if (m === null) return generic;
  const sign = m[1] ?? '';
  const whole = m[2] ?? '';
  const frac = m[3];
  // At least one digit, and no bare trailing dot ("5.").
  if (whole === '' && (frac === undefined || frac === '')) return generic;
  if (frac !== undefined && frac === '') return generic;
  if (frac !== undefined && frac.length > 2)
    return { ok: false, error: 'Use at most two decimals.' };
  if (whole.length > 6) return { ok: false, error: 'The highest raise is 1000%.' };
  // Whole digits then exactly two decimal digits: '2.5' -> '250'. Digits, not arithmetic.
  const magnitude = Number(`${whole === '' ? '0' : whole}${`${frac ?? ''}00`.slice(0, 2)}`);
  const bp = sign === '-' ? -magnitude : magnitude;
  if (bp <= -10_000) return { ok: false, error: 'A cut of 100% or more is not allowed.' };
  if (bp > MAX_RAISE_BP) return { ok: false, error: 'The highest raise is 1000%.' };
  // `-0` reads as 0.
  return { ok: true, bp: bp === 0 ? 0 : bp };
}

/**
 * DOLLARS: base + cents. PERCENT: mulDivHalfUp(base, 10000 + bp, 10000) (exact, half-up to the cent).
 * base must be a non-negative safe integer; bp < -10000 throws RangeError. The caller refuses results < 0
 * or over the ceiling.
 */
export function applyPriceChange(baseCents: number, change: PriceChange): number {
  if (!Number.isSafeInteger(baseCents) || baseCents < 0) {
    throw new RangeError(
      `applyPriceChange expects non-negative integer cents, got ${String(baseCents)}`,
    );
  }
  if (change.mode === 'DOLLARS') {
    if (!Number.isSafeInteger(change.cents)) {
      throw new RangeError(`applyPriceChange expects integer cents, got ${String(change.cents)}`);
    }
    return baseCents + change.cents;
  }
  const bp = change.basisPoints;
  if (!Number.isSafeInteger(bp)) {
    throw new RangeError(`applyPriceChange expects integer basis points, got ${String(bp)}`);
  }
  if (bp < -10_000) throw new RangeError('A cut of more than 100% is not allowed.');
  return mulDivHalfUp(baseCents, 10_000 + bp, 10_000);
}
